import Keycloak from 'keycloak-js';
import dlv from 'dlv';

/**
 * Enumerates props of the client config object supplied by Keycloak 
 * that do not match the arg names that keycloak-js expects.
 * 
 * This map maps them to the appropriate
 */
const CONFIG_MAP = {
	'auth-server-url':'url',
	'resource':'clientId'
}

/**
 * Use this function to remap client configs given by Keycloak, to args used by keycloak-js
 * @param {Object} config The client config object supplied by Keycloak.
 * @returns A config object properly configured for keycloak-js.
 */
function remapConfig(config) {
	if(!config)return;
	return Object.keys(config).reduce((acc, key) => {
		if(CONFIG_MAP[key]) {
			acc[CONFIG_MAP[key]] = config[key];
		} else {
			acc[key] = config[key];
		}
		return acc;
	}, {});

}
/**
 * Creates a keycloak-js client, and and streamlines it's usage.
 * @param {Object} config The configuration data passed to the keycloak-client.
 * @returns A streamlined API for acessing keycloak.
 */
export default function KeycloakClient(config, {Keycloak:KeycloakConf} = {}) {
	config = remapConfig(config);
	const client = KeycloakConf || Keycloak;
	const instance = client(config);
	
	//state:
	let authenticated = false,
		authData,
		profile;
	
	function auth(optional = false) {
		return authenticated && authData
			? Promise.resolve(authData) 
			: toPromise(
				instance.init({ 
					onLoad: optional ? 'check-sso': 'login-required', 
					checkLoginIframe: false 
				})
			).then(result => {
				authenticated = result;
				authData = { authenticated, ...instance.tokenParsed };
				return authData;
			});
	}

	function userProfile(...args) {
		return profile 
			? Promise.resolve(profile)
			: toPromise(instance.loadUserProfile(...args)).then(result => {
				profile = result;
				return profile;
			})
    }

	function isHOH() {
		return dlv(instance, 'tokenParsed.resource_access.account.roles', []).includes('hoh');
	}

	function logout() {
		return authenticated 
			? toPromise(instance.logout()).then(() => true)
			: toPromise(instance.init({ 
				onLoad: 'check-sso', 
				checkLoginIframe: false 
			})).then(r => r ? toPromise(instance.logout({redirectUri:window.location.origin})).then(() => true) : Promise.resolve(true))

	}

	function manage() {
		return authenticated 
			? toPromise(instance.accountManagement()).then(() => true)
			: Promise.resolve(false)
	}
	
	return { auth, logout, userProfile, manage, isHOH };
}

/**
 * Ensures that the returned object is a Promise.
 * If it is a Keycloak Promise, it is converted.
 * 
 * @param {Promise|Object} p A Keycloak Promise or a standard Promise.
 */
function toPromise(p) {
	return (p instanceof Promise) 
		? p
		: (p && p.success && p.error) 
			? new Promise((resolve, reject) => {
				p.success(resolve);
				p.error(reject);
			})
			: Promise.resolve(p);
}